'use client';

import { useState } from 'react';
import { ChevronRight, MessageSquare } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { quoteFollowUpRequest, type QuoteHistoryBlock } from '@/lib/chat/blocks/quote-history';

/**
 * One quote's history inside Mucka's bubble: a rail of what happened and when,
 * then "Ask Mucka to follow up" and "Open Q-1031".
 *
 * Every word and date comes off the block, which the server built from its
 * own read. Nothing here computes anything.
 *
 * 🚨 The follow-up tap ASKS, in the owner's words, and sends nothing. The
 * follow-up itself is prepared by the ordinary send path, with all its checks,
 * and its approval card shows the words that would go. That is why there are
 * no words here and why the tap is not orange: orange is for a tap that sends
 * or changes something.
 *
 * `live` is false while Mucka is answering and on an older reply: the follow-up
 * is a question about the quote as it stood then, so it is switched off.
 * Opening the quote is always allowed, as it is on the owed table.
 */
export function QuoteHistory({ block, live, onAnswer, onOpen }: {
  block: QuoteHistoryBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  const [asked, setAsked] = useState(false);
  return (
    <div className="mchat-qhist" role="group" aria-label={`${block.reference} history`}>
      <ol className="mchat-qhist-rail">
        {block.events.map((e, i) => (
          <li key={`${e.kind}-${i}`} className={`mchat-qhist-event mchat-qhist-event--${e.tone}${e.kind === 'more' ? ' mchat-qhist-event--more' : ''}`}>
            <span className="mchat-qhist-mark" aria-hidden="true">
              <span className="mchat-qhist-dot" />
            </span>
            <span className="mchat-qhist-text">
              <span className="mchat-qhist-what">{e.label}</span>
              {e.when && <span className="mchat-qhist-when">{e.when}</span>}
            </span>
          </li>
        ))}
      </ol>
      {block.note && <p className="mchat-qhist-note">{block.note}</p>}
      {block.canFollowUp && (
        <button
          type="button"
          className="mchat-row mchat-qhist-ask"
          disabled={!live || asked}
          onClick={() => { setAsked(true); onAnswer(quoteFollowUpRequest(block)); }}
        >
          <Icon icon={MessageSquare} size={15} className="mchat-row-icon" />
          <span className="mchat-row-main">
            <span className="mchat-row-title">{asked ? 'Asked Mucka to follow up' : 'Ask Mucka to follow up'}</span>
            <span className="mchat-row-detail">You&rsquo;ll see the words before anything is sent</span>
          </span>
        </button>
      )}
      <button type="button" className="mchat-row" onClick={() => onOpen(block.path)}>
        <span className="mchat-row-main">
          <span className="mchat-row-title">Open {block.reference}</span>
        </span>
        <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
      </button>
    </div>
  );
}
